// API Service Layer
// ==================

export const API_BASE = import.meta.env.DEV
  ? '/api'
  : 'https://sepom-api.ranchorayala.workers.dev/api';

export const fetcher = async (url: string) => {
  const fullUrl = url.startsWith('http') ? url : `${API_BASE}/${url}`;
  const res = await fetch(fullUrl);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error?.message || `Request failed with ${res.status}`);
  }
  return res.json();
};

export interface ApiResponse<T> {
  data: T;
  meta?: {
    total?: number;
    page?: number;
    per_page?: number;
  };
  error?: {
    code: string;
    message: string;
  };
}

async function handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Request failed');
  }
  return response.json();
}

// Politicians
export async function getPoliticians(params?: {
  page?: number;
  per_page?: number;
  party?: string;
  state?: string;
  position?: string;
  search?: string;
}) {
  const queryString = new URLSearchParams(params as any).toString();
  const response = await fetch(`${API_BASE}/politicians?${queryString}`);
  return handleResponse<any[]>(response);
}

export async function getPolitician(id: string) {
  const response = await fetch(`${API_BASE}/politicians/${id}`);
  return handleResponse(response);
}

export async function getPoliticianAttendance(id: string, year?: string) {
  const queryString = year ? `?year=${year}` : '';
  const response = await fetch(`${API_BASE}/politicians/${id}/attendance${queryString}`);
  return handleResponse(response);
}

export async function getPoliticianVotes(id: string, year?: string) {
  const queryString = year ? `?year=${year}` : '';
  const response = await fetch(`${API_BASE}/politicians/${id}/votes${queryString}`);
  return handleResponse(response);
}

export async function getPoliticianInitiatives(id: string, status?: string) {
  const queryString = status ? `?status=${status}` : '';
  const response = await fetch(`${API_BASE}/politicians/${id}/initiatives${queryString}`);
  return handleResponse(response);
}

export async function getPoliticianWorks(id: string) {
  const response = await fetch(`${API_BASE}/politicians/${id}/works`);
  return handleResponse(response);
}

// Parties
export async function getParties() {
  const response = await fetch(`${API_BASE}/parties`);
  return handleResponse(response);
}

export async function getParty(id: string) {
  const response = await fetch(`${API_BASE}/parties/${id}`);
  return handleResponse(response);
}

export async function getPartyStats(id: string) {
  const response = await fetch(`${API_BASE}/parties/${id}/stats`);
  return handleResponse(response);
}

// States
export async function getStates() {
  const response = await fetch(`${API_BASE}/states`);
  return handleResponse(response);
}

export async function getStateRepresentatives(id: string, level?: string) {
  const queryString = level ? `?level=${level}` : '';
  const response = await fetch(`${API_BASE}/states/${id}/representatives${queryString}`);
  return handleResponse(response);
}

// Initiatives
export async function getInitiatives(params?: {
  page?: number;
  per_page?: number;
  status?: string;
  politician?: string;
  party?: string;
}) {
  const queryString = new URLSearchParams(params as any).toString();
  const response = await fetch(`${API_BASE}/initiatives?${queryString}`);
  return handleResponse(response);
}

// Public Works
export async function getWorks(params?: {
  page?: number;
  per_page?: number;
  status?: string;
  category?: string;
  state?: string;
}) {
  const queryString = new URLSearchParams(params as any).toString();
  const response = await fetch(`${API_BASE}/works?${queryString}`);
  return handleResponse(response);
}

// Search
export async function search(query: string, type?: string) {
  const response = await fetch(`${API_BASE}/search`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ q: query, type }),
  });
  return handleResponse(response);
}

export async function searchNLP(query: string) {
  const response = await fetch(`${API_BASE}/search/nlp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ q: query }),
  });
  return handleResponse(response);
}

// Insights
export async function getInsights(limit?: number, type?: string) {
  const queryString = new URLSearchParams();
  if (limit) queryString.set('limit', limit.toString());
  if (type) queryString.set('type', type);
  const response = await fetch(`${API_BASE}/insights?${queryString.toString()}`);
  return handleResponse(response);
}

export async function submitInsightFeedback(id: string, helpful: boolean) {
  const response = await fetch(`${API_BASE}/insights/${id}/feedback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ helpful }),
  });
  return handleResponse(response);
}

// Compare
export async function comparePoliticians(id1: string, id2: string) {
  const response = await fetch(`${API_BASE}/compare/${id1}/${id2}`);
  return handleResponse(response);
}
